import { ClientResponseError } from 'pocketbase'
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { createDemoJourney, demoUser } from '../data/demo'
import * as api from '../lib/pocketbase'
import type { Activity, Comment, JourneyState, OnboardingInput, UserProfile } from '../types'

type Mode = 'checking' | 'pocketbase' | 'demo'

function isForbidden(error: unknown) {
  return error instanceof ClientResponseError && error.status === 403
}

interface AppContextValue {
  mode: Mode
  backendAvailable: boolean
  needsSubscription: boolean
  user: UserProfile | null
  journey: JourneyState | null
  comments: Comment[]
  busy: boolean
  error: string
  login: (email: string, password: string) => Promise<void>
  register: (name: string, email: string, password: string) => Promise<void>
  enterDemo: () => void
  logout: () => void
  finishOnboarding: (data: OnboardingInput) => Promise<void>
  progress: (activityId: string, action: string, data?: Record<string, unknown>) => Promise<void>
  reflect: (text: string) => Promise<void>
  loadComments: (activityId: string) => Promise<void>
  postComment: (activityId: string, text: string, parentId?: string) => Promise<void>
  toggleLike: (commentId: string) => Promise<void>
  refresh: () => Promise<void>
}

const Context = createContext<AppContextValue | null>(null)
const STORAGE_KEY = 'mcv-demo-state-v1'
const COMMENTS_KEY = 'mcv-demo-comments-v1'

function seedDemoComments(): Comment[] {
  const now = Date.now()
  return [
    {
      id: 'demo-c1', text: 'Nunca tinha reparado como as quatro notas voltam disfarçadas em outros instrumentos. Ouvi de novo só prestando atenção nisso.', createdAt: new Date(now - 3 * 3600000).toISOString(), parentId: null,
      user: { id: 'demo-listener-1', name: 'Marina', isBotelho: false, isTeam: false }, likeCount: 2, likedByMe: false,
    },
    {
      id: 'demo-c2', text: 'O que fica pra mim é como um gesto tão curto consegue segurar um movimento inteiro sem cansar. Isso muda como eu vou ouvir a próxima sinfonia também.', createdAt: new Date(now - 2 * 3600000).toISOString(), parentId: null,
      user: { id: 'demo-botelho', name: 'Leandro Botelho', isBotelho: true, isTeam: false }, likeCount: 5, likedByMe: false,
    },
    {
      id: 'demo-c3', text: 'Boa observação — eu senti isso mais forte na terceira escuta, quando já não estava tentando "entender" nada.', createdAt: new Date(now - 1 * 3600000).toISOString(), parentId: 'demo-c2',
      user: { id: 'demo-listener-2', name: 'Diego', isBotelho: false, isTeam: false }, likeCount: 1, likedByMe: false,
    },
  ]
}

function loadDemoComments(activityId: string): Comment[] {
  try {
    const all = JSON.parse(localStorage.getItem(COMMENTS_KEY) || '{}') as Record<string, Comment[]>
    if (all[activityId]) return all[activityId]
    const seeded = seedDemoComments()
    all[activityId] = seeded
    localStorage.setItem(COMMENTS_KEY, JSON.stringify(all))
    return seeded
  } catch { return seedDemoComments() }
}

function saveDemoComments(activityId: string, comments: Comment[]) {
  try {
    const all = JSON.parse(localStorage.getItem(COMMENTS_KEY) || '{}') as Record<string, Comment[]>
    all[activityId] = comments
    localStorage.setItem(COMMENTS_KEY, JSON.stringify(all))
  } catch { /* ignore */ }
}

function applySequenceGating(activities: Activity[]): Activity[] {
  let priorComplete = true
  return activities.map((activity) => {
    if (activity.status === 'completed') { priorComplete = true; return activity }
    const unlocked = priorComplete
    priorComplete = false
    return { ...activity, status: unlocked ? (activity.status === 'in_progress' ? 'in_progress' : 'available') : 'locked' }
  })
}

function loadDemo() {
  const stored = localStorage.getItem(STORAGE_KEY)
  if (!stored) return createDemoJourney()
  try { return JSON.parse(stored) as JourneyState } catch { return createDemoJourney() }
}

export function AppProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<Mode>('checking')
  const [backendAvailable, setBackendAvailable] = useState(false)
  const [needsSubscription, setNeedsSubscription] = useState(false)
  const [user, setUser] = useState<UserProfile | null>(null)
  const [journey, setJourney] = useState<JourneyState | null>(null)
  const [comments, setComments] = useState<Comment[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const refresh = useCallback(async () => {
    if (mode === 'demo') {
      const next = loadDemo()
      next.serverNow = new Date().toISOString()
      next.activities = applySequenceGating(next.activities)
      setJourney(next)
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
      return
    }
    if (mode === 'pocketbase' && api.pb.authStore.isValid) {
      try { setJourney(await api.getJourney()); setNeedsSubscription(false) }
      catch (cause) { if (isForbidden(cause)) setNeedsSubscription(true); else throw cause }
    }
  }, [mode])

  useEffect(() => {
    void (async () => {
      const available = await api.isPocketBaseAvailable()
      setBackendAvailable(available)
      setMode('pocketbase')
      if (available && api.pb.authStore.isValid) {
        setUser(api.currentProfile())
        try { setJourney(await api.getJourney()) }
        catch (cause) { if (isForbidden(cause)) setNeedsSubscription(true); else api.pb.authStore.clear() }
      }
    })()
  }, [])

  useEffect(() => {
    if (mode !== 'demo') return
    const timer = window.setInterval(() => void refresh(), 15000)
    return () => window.clearInterval(timer)
  }, [mode, refresh])

  const perform = async (work: () => Promise<void>) => {
    setBusy(true); setError('')
    try { await work() } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Não foi possível concluir. Tente outra vez.')
      throw cause
    } finally { setBusy(false) }
  }

  const value = useMemo<AppContextValue>(() => ({
    mode, backendAvailable, needsSubscription, user, journey, comments, busy, error,
    login: async (email, password) => perform(async () => {
      if (!backendAvailable) throw new Error('Sem conexão no momento. Tente de novo em instantes ou use a demonstração.')
      setMode('pocketbase'); setUser(await api.signIn(email, password))
      try { setJourney(await api.getJourney()); setNeedsSubscription(false) }
      catch (cause) { if (isForbidden(cause)) setNeedsSubscription(true); else throw cause }
    }),
    register: async (name, email, password) => perform(async () => {
      if (!backendAvailable) throw new Error('Sem conexão no momento. Tente de novo em instantes ou use a demonstração.')
      setMode('pocketbase'); setUser(await api.signUp(name, email, password)); setJourney(null)
    }),
    enterDemo: () => { setMode('demo'); setUser(demoUser); setJourney(loadDemo()); setError('') },
    logout: () => { api.pb.authStore.clear(); setUser(null); setJourney(null); setMode('pocketbase') },
    finishOnboarding: async (data) => perform(async () => {
      if (mode === 'demo') {
        const nextUser = { ...demoUser, ...data, onboardingComplete: true }
        setUser(nextUser); setJourney(loadDemo()); return
      }
      setUser(await api.saveOnboarding(data))
      try { setJourney(await api.getJourney()); setNeedsSubscription(false) }
      catch (cause) { if (isForbidden(cause)) setNeedsSubscription(true); else throw cause }
    }),
    progress: async (activityId, action, data = {}) => perform(async () => {
      if (mode === 'demo') {
        const next = structuredClone(journey || loadDemo())
        const activity = next.activities.find((item) => item.id === activityId)
        if (!activity || activity.status === 'locked') throw new Error('Este encontro ainda não está disponível.')
        if (action === 'position') activity.progressSeconds = Number(data.position || 0)
        if (action === 'start' && activity.status !== 'completed') activity.status = 'in_progress'
        if (action === 'complete') activity.status = 'completed'
        next.activities = applySequenceGating(next.activities)
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); setJourney(next); return
      }
      setJourney(await api.mutateProgress(activityId, action, data))
    }),
    reflect: async (text) => perform(async () => {
      if (mode === 'demo') {
        const next = structuredClone(journey || loadDemo()); next.reflection = text
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); setJourney(next); return
      }
      setJourney(await api.saveReflection(text))
    }),
    loadComments: async (activityId) => {
      if (mode === 'demo') { setComments(loadDemoComments(activityId)); return }
      try { setComments(await api.getComments(activityId)) } catch { setComments([]) }
    },
    postComment: async (activityId, text, parentId) => perform(async () => {
      if (mode === 'demo') {
        const trimmed = text.trim()
        if (!trimmed) return
        const next: Comment = {
          id: `demo-${Date.now()}`, text: trimmed, createdAt: new Date().toISOString(), parentId: parentId || null,
          user: { id: user?.id || 'demo-user', name: user?.name || 'Você', isBotelho: false, isTeam: false }, likeCount: 0, likedByMe: false,
        }
        const updated = [...loadDemoComments(activityId), next]
        saveDemoComments(activityId, updated); setComments(updated); return
      }
      setComments(await api.postComment(activityId, text, parentId))
    }),
    toggleLike: async (commentId) => perform(async () => {
      if (mode === 'demo') {
        const updated = comments.map((item) => item.id === commentId ? { ...item, likedByMe: !item.likedByMe, likeCount: item.likeCount + (item.likedByMe ? -1 : 1) } : item)
        setComments(updated)
        const all = JSON.parse(localStorage.getItem(COMMENTS_KEY) || '{}') as Record<string, Comment[]>
        const activityId = Object.keys(all).find((key) => all[key].some((item) => item.id === commentId))
        if (activityId) saveDemoComments(activityId, updated)
        return
      }
      setComments(await api.toggleCommentLike(commentId))
    }),
    refresh,
  }), [mode, backendAvailable, needsSubscription, user, journey, comments, busy, error, refresh])

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export function useApp() {
  const value = useContext(Context)
  if (!value) throw new Error('useApp precisa estar dentro de AppProvider')
  return value
}
